import { RadioChangeEvent, Space } from 'antd';
import { snakeCase } from 'lodash';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ChannelMappingOptions } from 'api/integrations/slack/slack.types';
import { Button } from 'components/atoms/button';
import { Radio } from 'components/atoms/radio';
import { Option } from 'components/atoms/select';
import { SectionLayout } from '../common';
import { ChannelSelectionModal } from './channel-selection-modal/channel-selection-modal';
import { RadioContent, ModalTriggerContainer } from './channel-settings.styles';
import { ChannelSettingTypes } from './channel-settings.types';
import { channelSettings } from './constants';

const ModalTrigger = ({
  onClick,
  label,
}: {
  label?: string;
  onClick: () => void;
}) => {
  return (
    <ModalTriggerContainer>
      <FormattedMessage id="settings.integrations.slack.channel_settings.fixed.trigger.text" />
      <Button
        type="link"
        onClick={onClick}
        data-cy="integration.slack.select.channel"
      >
        {label ?? <FormattedMessage id="choose" />}
      </Button>
    </ModalTriggerContainer>
  );
};

export const ChannelSettings = ({
  settings,
  loading,
  handleChange,
  channelsList,
}: ChannelSettingTypes) => {
  const [modalVisibility, setModalVisibility] = useState(false);

  const handleModalTrigger = useCallback(() => {
    setModalVisibility(true);
  }, []);

  const handleCancel = () => {
    setModalVisibility(false);
  };

  const handleChannelChange = (e: RadioChangeEvent) => {
    const {
      target: { value },
    } = e;

    if (value === ChannelMappingOptions.NewChannel) {
      if (settings) {
        return handleChange?.({
          ...settings,
          channelMappingMechanism: value,
        });
      }
    }
    if (value === ChannelMappingOptions.FixedChannel) {
      return setModalVisibility(true);
    }

    return false;
  };

  const handleChannelSelection = (data?: Option) => {
    handleCancel();
    if (settings) {
      handleChange?.({
        ...settings,
        channelMappingMechanism: data
          ? ChannelMappingOptions.FixedChannel
          : ChannelMappingOptions.NewChannel,
        fixedChannel: data,
      });
    }
  };

  return (
    <SectionLayout
      title={
        <FormattedMessage id="settings.integrations.slack.channel_settings.title" />
      }
    >
      <Radio.Group
        onChange={handleChannelChange}
        disabled={settings == null}
        value={settings?.channelMappingMechanism}
      >
        <Space direction="vertical">
          {channelSettings.map(({ value, key }) => (
            <Radio
              value={value}
              data-cy={
                value === 'CREATE_NEW_PER_PROJECT'
                  ? 'integrations.slack.new_project_check_box'
                  : 'integrations.slack.fixed_checkbox'
              }
            >
              <RadioContent>
                <p>
                  <FormattedMessage
                    id={`settings.integrations.slack.channel_settings.${snakeCase(
                      key,
                    )}`}
                  />
                </p>
                <small>
                  <FormattedMessage
                    id={`settings.integrations.slack.channel_settings.${snakeCase(
                      key,
                    )}.helper`}
                    values={{
                      b: (chunks) => <b>{chunks}</b>,
                    }}
                  />
                </small>
              </RadioContent>
            </Radio>
          ))}
        </Space>
      </Radio.Group>

      {settings?.channelMappingMechanism ===
      ChannelMappingOptions.FixedChannel ? (
        <ModalTrigger
          onClick={handleModalTrigger}
          label={settings?.fixedChannel?.label}
        />
      ) : null}

      {modalVisibility && settings ? (
        <ChannelSelectionModal
          onSelect={handleChannelSelection}
          onClose={handleCancel}
          value={settings.fixedChannel}
          channelsList={channelsList}
          settings={settings}
          loading={loading}
        />
      ) : null}
    </SectionLayout>
  );
};
